import {
  AUTH_COOKIE_SUFFIX,
  getAuthCookieName,
  getSessionDataCookieName,
  getSessionTokenCookieName,
  isSiteId,
  resolveAuthCookiePrefix,
} from '@sizzlenz/shared';
import type { SiteId } from '@sizzlenz/shared';

/**
 * Client-side view of the auth cookie names.
 *
 * The API sets `advanced.cookiePrefix` to the site id, so its cookies are named
 * `<siteId>.session_token` rather than the Better Auth default
 * `better-auth.session_token`. `createAuthClient` has no cookie option of its own
 * (the browser handles the cookie jar), so these helpers exist for the places that
 * do have to name a cookie: quick "is there a session?" checks, SSR/middleware
 * reads, and manual clean-up.
 *
 * Resolution order for the site id:
 *   1. explicit value passed to {@link configureAuthCookies}
 *   2. NEXT_PUBLIC_SITE_ID
 *   3. SITE_ID
 *   4. SIZZLE_PLATFORM (the older name, still set in the Sizzle frontend env)
 *
 * Anything unrecognised falls back to Better Auth's default prefix so the client
 * never invents a name the server isn't using.
 */

interface AuthCookieConfig {
  siteId?: string | null;
  /** Explicit prefix override, mirrors the API's AUTH_COOKIE_PREFIX env var. */
  prefix?: string | null;
}

let config: AuthCookieConfig = {};

/**
 * Override how cookie names are resolved. Call once during app bootstrap when the
 * site id isn't available through the environment variables above.
 */
export function configureAuthCookies(next: AuthCookieConfig): void {
  config = { ...config, ...next };
}

function siteIdFromEnv(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_SITE_ID ?? process.env.SITE_ID ?? process.env.SIZZLE_PLATFORM
  )?.trim();
}

/** The site this frontend is serving, or undefined when it isn't configured. */
export function getSiteId(): SiteId | undefined {
  const candidate = config.siteId ?? siteIdFromEnv();
  return isSiteId(candidate) ? candidate : undefined;
}

/** Cookie prefix the API is using, e.g. "sizzlenz". */
export function getAuthCookiePrefix(): string {
  return resolveAuthCookiePrefix({ siteId: getSiteId(), prefix: config.prefix });
}

/**
 * Session token cookie name, e.g. "sizzlenz.session_token".
 * Pass `secure` to get the `__Secure-` variant the API sets over HTTPS.
 */
export function getAuthSessionCookieName(options: { secure?: boolean } = {}): string {
  return getSessionTokenCookieName({
    siteId: getSiteId(),
    prefix: config.prefix,
    secure: options.secure,
  });
}

/** Cached session data cookie name, e.g. "sizzlenz.session_data". */
export function getAuthSessionDataCookieName(options: { secure?: boolean } = {}): string {
  return getSessionDataCookieName({
    siteId: getSiteId(),
    prefix: config.prefix,
    secure: options.secure,
  });
}

/**
 * Read a cookie in the browser. Returns undefined during SSR.
 * Names are matched exactly, so pass a value from the helpers above.
 */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const target = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const entry = part.trim();
    if (entry.startsWith(target)) {
      return decodeURIComponent(entry.slice(target.length));
    }
  }
  return undefined;
}

/**
 * True when a session cookie for this site is present.
 *
 * A cheap client-side hint only — the cookie is httpOnly in some configurations
 * and its presence says nothing about whether the session is still valid. Use
 * `useSession()` for anything that matters.
 */
export function hasAuthSessionCookie(): boolean {
  return (
    readCookie(getAuthSessionCookieName()) !== undefined ||
    readCookie(getAuthSessionCookieName({ secure: true })) !== undefined
  );
}

/**
 * Names of every auth cookie this site uses, both plain and `__Secure-` variants.
 * Useful for debugging or clearing local state after a forced sign-out.
 */
export function getAuthCookieNames(): string[] {
  const siteId = getSiteId();
  const { prefix } = config;
  return Object.values(AUTH_COOKIE_SUFFIX).flatMap((suffix) => [
    getAuthCookieName(suffix, { siteId, prefix }),
    getAuthCookieName(suffix, { siteId, prefix, secure: true }),
  ]);
}
